import { useAppDispatch, useAppSelector } from '../../../store/store';
import {
  createSchedule,
  selectClinicSettingsDetail,
  updateClinicSettings,
  updateDispensarizationSettings,
  updateFormSettings,
  updateNotificationSettings,
  updateSlotSettings,
} from '../../../store/reducers/ClinicSettingsDetailSlice';
import { localToWeeklySchedule } from './schedule';
import type { ScheduleDay } from './schedule';
import { minutesToTimespan } from './callSettings';
import type { DirtyScenario } from './callSettings';
import type { FormSnapshot } from './types';

export const useClinicSettingsMutations = (
  clinicId: string | undefined,
  currentSnapshot: FormSnapshot,
) => {
  const dispatch = useAppDispatch();
  const item = useAppSelector(selectClinicSettingsDetail);

  const createNewSchedule = (days: ScheduleDay[], holidays: string[]) =>
    dispatch(createSchedule(localToWeeklySchedule(days, holidays))).unwrap();

  const saveClinic = async (isScheduleDirty: boolean) => {
    if (!clinicId) return;
    const operatorScheduleId = isScheduleDirty
      ? await createNewSchedule(
          currentSnapshot.schedule,
          currentSnapshot.holidays,
        )
      : (item?.operatorScheduleId ?? null);
    await dispatch(
      updateClinicSettings({
        clinicId,
        data: {
          operatorPhone: currentSnapshot.operatorPhone.trim() || '0',
          operatorScheduleId,
          sipAccountId: currentSnapshot.sipAccountId,
          callPoolId: currentSnapshot.callPoolId,
        },
      }),
    ).unwrap();
  };

  const patchScenario = async (
    scenario: DirtyScenario,
    newScheduleId?: string,
  ) => {
    if (!clinicId) return;
    const data = {
      callsMaxTries: scenario.callsMaxTries,
      callsInterval: minutesToTimespan(scenario.callsIntervalMinutes),
      callsDisable: scenario.callsDisable,
      callsScheduleId: newScheduleId ?? scenario.callsScheduleId,
    };
    switch (scenario.type) {
      case 'slot':
        await dispatch(
          updateSlotSettings({
            clinicId,
            data: {
              ...data,
              callsExcludedSpecializationIds:
                currentSnapshot.excludedSpecializationIds,
              callsExcludedDoctorIds: currentSnapshot.excludedDoctorIds,
            },
          }),
        ).unwrap();
        return;
      case 'notification':
        await dispatch(updateNotificationSettings({ clinicId, data })).unwrap();
        return;
      case 'dispensarization':
        await dispatch(
          updateDispensarizationSettings({ clinicId, data }),
        ).unwrap();
        return;
      case 'form':
        await dispatch(updateFormSettings({ clinicId, data })).unwrap();
        return;
      default: {
        const unsupported: never = scenario.type;
        throw new RangeError(`Unsupported scenario: ${unsupported}`);
      }
    }
  };

  return { saveClinic, createNewSchedule, patchScenario };
};
